import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Gift, Ticket } from "lucide-react";
import { GameModal } from "../../components/GameModal";
import { PublicLayout } from "../../components/PublicLayout";
import { api } from "../../lib/api";
import { isLocalDevFrontend } from "../../lib/env";
import { shortDate } from "../../lib/format";

interface WeekendBonusTask {
  task_code: string;
  title: string;
  description: string;
  action_label: string;
  reward_text: string;
  status: string;
  status_label: string;
  completed_at?: string | null;
}

interface WeekendBonusReward {
  code: string;
  title: string;
  voucher_type: string;
  status: string;
  status_label: string;
  voucher_id?: number | null;
}

interface WeekendBonusStatus {
  is_available: boolean;
  locked_reason?: string | null;
  display_message: string;
  weekend_id: string;
  weekend_start_at: string;
  weekend_end_at: string;
  next_weekend_start_at: string;
  remaining_seconds: number;
  tasks: WeekendBonusTask[];
  rewards: WeekendBonusReward[];
}

const actionMap: Record<string, string> = {
  weekend_checkin: "/api/public/weekend-bonus/checkin",
  weekend_single_1000: "/api/public/weekend-bonus/check-single-deposit",
  weekend_cumulative_5000: "/api/public/weekend-bonus/check-cumulative-deposit"
};

const WEEKEND_TEST_NOW = "2026-07-04T10:00:00+08:00";

export function WeekendBonusPage() {
  const initialParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [account] = useState(
    () => initialParams.get("member_account") ?? initialParams.get("account") ?? localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? ""
  );
  const [appliedTestNow] = useState(() => isLocalDevFrontend() ? initialParams.get("test_now") ?? "" : "");
  const [status, setStatus] = useState<WeekendBonusStatus | null>(null);
  const [loadingAction, setLoadingAction] = useState("");
  const [modal, setModal] = useState<{ title: string; text: string; tone?: "info" | "success" | "error" | "reward" } | null>(null);
  const isLocalDev = isLocalDevFrontend();
  const showTestNowTools = isLocalDev && /^test/i.test(account);

  async function load() {
    if (!account) return;
    const params = new URLSearchParams({ member_account: account });
    if (appliedTestNow) params.set("test_now", appliedTestNow);
    setStatus(await api<WeekendBonusStatus>(`/api/public/weekend-bonus?${params.toString()}`));
  }

  async function runTask(task: WeekendBonusTask) {
    const endpoint = actionMap[task.task_code];
    if (!endpoint || loadingAction) return;
    setLoadingAction(task.task_code);
    try {
      const result = await api<{ status: WeekendBonusStatus }>(endpoint, {
        method: "POST",
        body: JSON.stringify({ member_account: account, ...(appliedTestNow ? { test_now: appliedTestNow } : {}) })
      });
      setStatus(result.status);
      setModal({ title: "週末耀旺完成", text: `${task.title} 已完成。`, tone: "success" });
    } catch (error) {
      setModal({ title: "週末耀旺尚未完成", text: error instanceof Error ? error.message : "操作失敗", tone: "error" });
    } finally {
      setLoadingAction("");
    }
  }

  useEffect(() => {
    load().catch((error) => setModal({ title: "讀取失敗", text: error instanceof Error ? error.message : "週末耀旺資料讀取失敗", tone: "error" }));
  }, [account, appliedTestNow]);

  function applyWeekendTestNow() {
    const params = new URLSearchParams(window.location.search);
    params.set("member_account", account);
    params.set("test_now", WEEKEND_TEST_NOW);
    window.location.href = `/weekend-bonus?${params.toString()}`;
  }

  return (
    <PublicLayout title="週末耀旺" subtitle="週末限定紅包券任務">
      <section className="mission-focus page-focus red-packet-hero-panel">
        <span className="hero-kicker">週末耀旺</span>
        <h2>週末限定紅包券任務</h2>
        <p>週末完成查看與充值任務，可獲得週末紅包券。紅包券本身不進派發中心，未來抽中河幣後才會送入派發中心。</p>
        <div className="gems-balance-card">
          <span>活動狀態</span>
          <strong>{status?.is_available ? "開放中" : "尚未開啟"}</strong>
        </div>
        {appliedTestNow ? <p className="admin-muted">local/dev test_now：{appliedTestNow}</p> : null}
      </section>

      {showTestNowTools ? (
        <section className="action-panel">
          <div className="admin-card-head">
            <span>local/dev 週末耀旺驗收</span>
            <h2>套用週末測試時間</h2>
          </div>
          <p>測試帳號：{account}</p>
          <p>測試時間：{appliedTestNow || WEEKEND_TEST_NOW}</p>
          <button type="button" className="secondary-cta" onClick={applyWeekendTestNow}>
            套用週末測試時間
          </button>
        </section>
      ) : null}

      {!account ? (
        <section className="action-panel">
          <h2>請先完成會員綁定</h2>
          <p>綁定會員後，才能查看週末耀旺活動。</p>
          <a className="primary-cta" href="/">返回大廳</a>
        </section>
      ) : null}

      {account && status && !status.is_available ? (
        <section className="action-panel weekend-waiting-card">
          <span className="status-pill locked">尚未開啟</span>
          <h2>週末耀旺即將開啟</h2>
          <p>{status.display_message}</p>
          <div className="weekly-pass-lock-grid">
            <div><span>本次週末期間</span><strong>{shortDate(status.weekend_start_at)} 至 {shortDate(status.weekend_end_at)}</strong></div>
            <div><span>下一次開啟</span><strong>{shortDate(status.next_weekend_start_at)}</strong></div>
            <div><span>剩餘時間</span><strong>{formatRemainingSeconds(status.remaining_seconds)}</strong></div>
          </div>
          <a className="secondary-cta" href="/">返回龍來喬大廳</a>
        </section>
      ) : null}

      {account && status?.is_available ? (
        <>
          <section className="action-panel">
            <div className="admin-card-head">
              <span>週末期間</span>
              <h2>{shortDate(status.weekend_start_at)} 至 {shortDate(status.weekend_end_at)}</h2>
            </div>
            <p>剩餘時間：{formatRemainingSeconds(status.remaining_seconds)}</p>
          </section>

          <section className="exchange-grid gems-exchange-grid">
            {status.tasks.map((task) => {
              const canRun = task.status === "available";
              return (
                <article className={`exchange-card ${task.status === "completed" ? "redeemable" : canRun ? "active" : "locked"}`} key={task.task_code}>
                  <div className="exchange-icon-orb">
                    {task.task_code === "weekend_checkin" ? <CalendarDays size={26} /> : task.task_code === "weekend_single_1000" ? <Ticket size={26} /> : <Gift size={26} />}
                  </div>
                  <div className="exchange-card-main">
                    <span className="hero-kicker">{task.status_label}</span>
                    <h3>{task.title}</h3>
                    <strong>{task.reward_text}</strong>
                    <p>{task.description}</p>
                    {task.completed_at ? <p className="admin-muted">完成時間：{shortDate(task.completed_at)}</p> : null}
                  </div>
                  <div className="exchange-card-action">
                    <span className={`status-tag ${task.status === "completed" ? "active" : canRun ? "claim" : "locked"}`}>{task.status_label}</span>
                    {task.task_code === "weekend_checkin" ? (
                      <a className="secondary-cta task-guide-link" href="/group-checkin">查看打卡方式</a>
                    ) : null}
                    <button type="button" disabled={!canRun || Boolean(loadingAction)} onClick={() => runTask(task)}>
                      {loadingAction === task.task_code ? "處理中" : task.action_label}
                    </button>
                  </div>
                </article>
              );
            })}
          </section>

          <section className="action-panel">
            <div className="admin-card-head">
              <span>獎勵狀態</span>
              <h2>週末紅包券</h2>
            </div>
            <div className="summary-list">
              {status.rewards.map((reward) => (
                <p key={reward.code}>{reward.title}：{reward.status_label}{reward.voucher_id ? `，券 ID ${reward.voucher_id}` : ""}</p>
              ))}
            </div>
          </section>
        </>
      ) : null}

      {modal ? <GameModal open title={modal.title} tone={modal.tone ?? "info"} onClose={() => setModal(null)}>{modal.text}</GameModal> : null}
    </PublicLayout>
  );
}

function formatRemainingSeconds(seconds?: number | null) {
  const value = Math.max(0, Number(seconds ?? 0));
  const days = Math.floor(value / 86400);
  const hours = Math.floor((value % 86400) / 3600);
  if (days > 0) return `${days} 天 ${hours} 小時`;
  const minutes = Math.floor((value % 3600) / 60);
  return `${hours} 小時 ${minutes} 分`;
}
